import axios from 'axios';
import { API_CONFIG } from '@/config/api';
import { getAuditToken, clearAuditToken } from '@/utils/auditAuth';

/**
 * Audit 专用 HTTP 实例
 *
 * 用于调用 Audit 项目的业务接口（订阅、席位管理等）。
 * 与 @/utils/http 的区别：本实例自动附带 Audit 签发的令牌，
 * 并在收到 401 时清除缓存并重试一次。
 */
const instance = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? '' : API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器：附带 Audit 令牌
instance.interceptors.request.use(
  async config => {
    const token = await getAuditToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// 响应拦截器：解包数据 & 401 重试一次
instance.interceptors.response.use(
  response => response.data || response,
  async error => {
    const originalRequest = error.config;

    // Audit 令牌失效：清除缓存后换取新令牌并重试一次
    if (
      error.response &&
      error.response.status === 401 &&
      originalRequest &&
      !originalRequest.__auditRetried
    ) {
      originalRequest.__auditRetried = true;
      clearAuditToken();
      try {
        const newToken = await getAuditToken();
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (retryError) {
        return Promise.reject(retryError);
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
